import { useState } from 'react';
import type { ReactElement } from 'react';
import { ROLE_LABELS, type Role } from '@fleet/shared';
import { useChangeNameMutation, useChangePasswordMutation } from '@/api/endpoints';
import { useSession } from '@/app/session';
import { Button } from '@/design/Button';
import { Identifier } from '@/design/Data';
import { Field, TextInput } from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';

/**
 * Your own account.
 *
 * Its own screen rather than a panel on Settings, because Settings is the
 * *business* and only an owner can open it — while everyone who signs in needs
 * to be able to change their own password. QA reported it simply missing: an
 * account's password could only ever be the one it was created with, so a
 * temporary password handed over on day one stayed the password for ever.
 *
 * The phone number is not editable here. It is the login, it is globally
 * unique across every business on the platform, and changing it is an owner's
 * decision about a staff account rather than a personal preference.
 */
export function AccountPage(): ReactElement {
  const session = useSession();
  const toast = useToast();

  const [name, setName] = useState(session.name);
  const [changeName, renaming] = useChangeNameMutation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [mismatch, setMismatch] = useState('');
  const [changePassword, changing] = useChangePasswordMutation();

  const nameProblems = readApiError(renaming.error);
  const passwordProblems = readApiError(changing.error);

  async function rename(): Promise<void> {
    try {
      const saved = await changeName({ name }).unwrap();
      toast.done(`You are now ${saved.name}.`);
    } catch {
      // Rendered under the field by `nameProblems`.
    }
  }

  async function replacePassword(): Promise<void> {
    setMismatch('');
    // Typed twice because it is typed blind, and a password set with a typo
    // in it locks the person out of the account they just changed.
    if (next !== again) {
      setMismatch('The two new passwords are not the same.');
      return;
    }

    try {
      await changePassword({ current_password: current, new_password: next }).unwrap();
      setCurrent('');
      setNext('');
      setAgain('');
      toast.done('Your password has been changed. You are still signed in.');
    } catch {
      // Rendered under the field by `passwordProblems`.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">My account</h1>

      <Panel title="Who you are" note="Your name is what the audit trail records against" current>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void rename();
          }}
        >
          <PanelBody>
            <div className="flex flex-col gap-4">
              <Field label="Name" required error={nameProblems.fields['name']}>
                {({ id, describedBy }) => (
                  <TextInput id={id} describedBy={describedBy} value={name} onChange={setName} />
                )}
              </Field>

              <dl className="divide-y divide-rule border-y border-rule">
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-ink-muted">Phone number (your login)</dt>
                  <dd>
                    <Identifier>{session.phone}</Identifier>
                  </dd>
                </div>
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-ink-muted">Role</dt>
                  <dd>{ROLE_LABELS[session.role as Role]}</dd>
                </div>
              </dl>
              <p className="text-small text-ink-muted">
                To change your phone number or your role, ask the owner.
              </p>

              {nameProblems.detail !== undefined && (
                <p className="font-medium text-alert" role="alert">
                  {nameProblems.detail}
                </p>
              )}

              <div>
                <Button
                  intent="primary"
                  size="touch"
                  type="submit"
                  disabled={renaming.isLoading || name.trim() === session.name}
                >
                  {renaming.isLoading ? 'Saving' : 'Save name'}
                </Button>
              </div>
            </div>
          </PanelBody>
        </form>
      </Panel>

      <Panel title="Password" note="Change it whenever you like. Nobody else can see it.">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void replacePassword();
          }}
        >
          <PanelBody>
            <div className="flex max-w-md flex-col gap-4">
              {/* Asked for even though you are already signed in: this is a
                  shared counter machine, and without it anyone walking past a
                  session somebody left open could lock the owner out. */}
              <Field
                label="Your current password"
                required
                error={passwordProblems.fields['current_password']}
              >
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    type="password"
                    autoComplete="current-password"
                    value={current}
                    onChange={setCurrent}
                  />
                )}
              </Field>

              <Field label="New password" required error={passwordProblems.fields['new_password']}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={setNext}
                  />
                )}
              </Field>

              <Field label="New password again" required error={mismatch || undefined}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    type="password"
                    autoComplete="new-password"
                    value={again}
                    onChange={setAgain}
                  />
                )}
              </Field>

              {passwordProblems.detail !== undefined && (
                <p className="font-medium text-alert" role="alert">
                  {passwordProblems.detail}
                </p>
              )}

              <div>
                <Button
                  intent="primary"
                  size="touch"
                  type="submit"
                  disabled={changing.isLoading || current === '' || next === ''}
                >
                  {changing.isLoading ? 'Changing' : 'Change password'}
                </Button>
              </div>
            </div>
          </PanelBody>
        </form>
      </Panel>
    </div>
  );
}
